import React from 'react';
import { RouteConfig } from 'react-router-config';
import { Redirect } from 'react-router-dom';

import { HasToBeClientGuard } from '../../guards/HasToBeClientGuard';
import { HasToBeLoggedInGuard } from '../../guards/HasToBeLoggedInGuard';

const PREFIX = '/client/';

const clientRoutes: Array<RouteConfig> = [
  {
    path: [PREFIX + 'job-create/:type'],
    component: React.lazy(
      () => import('../../../main/client/job-create/JobCreatePage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: [PREFIX + 'job-create'],
    component: React.lazy(
      () => import('../../../main/client/job-create/JobCreatePage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: PREFIX + 'find-agency/:id',
    component: React.lazy(
      () => import('../../../main/client/find-agency/FindAgency')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  {
    path: '/client',
    exact: true,
    component: () => <Redirect to={'/client/dashboard'} />,
  },
  {
    path: '/client/dashboard',
    component: React.lazy(
      () => import('../../../main/client/dashboard/ClientDashboard')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  // BOOKINGS SECTION
  {
    path: '/client/bookings',
    exact: true,
    component: () => <Redirect to={'/client/bookings/pending-matches'} />,
  },
  {
    path: '/client/bookings/book-agency/:leadId',
    exact: true,
    component: React.lazy(
      () =>
        import('../../../main/client/bookings/agency-booking/AgencyBookingPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/invoices/:invoiceId',
    component: React.lazy(
      () =>
        import('../../../main/client/bookings/details/ClientInvoiceDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/invoices/:invoiceId/receipt',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/details/AgencyReceiptDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/details/:leadId',
    component: React.lazy(
      () =>
        import('../../../main/client/bookings/details/ClientBookingDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/proposal/:leadId',
    component: React.lazy(
      () => import('../../../main/client/bookings/proposal/ClientProposalPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/report/:reportId',
    component: React.lazy(
      () =>
        import(
          '../../../main/client/bookings/report-details/ClientReportDetailsPageWrapper'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/report/:reportId',
    component: React.lazy(
      () =>
        import(
          '../../../main/client/bookings/report-details/ClientReportDetailsPageWrapper'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId/report/:reportId/receipt',
    component: React.lazy(
      () =>
        import(
          '../../../main/agency/bookings/details/EventReportReceiptDetailsPage'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/bookings/:sectionId',
    component: React.lazy(
      () => import('../../../main/client/bookings/ClientBookingsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  // END BOOKINGS SECTION

  // MESSAGES SECTION
  {
    path: '/cmi',
    exact: true,
    component: () => <Redirect to={'/client/messaging/inbox'} />,
  },
  {
    path: '/cmi/:id',
    exact: true,
    component: (props: any) => (
      <Redirect to={`/client/messaging/inbox/${props.match.params.id}`} />
    ),
  },
  {
    path: '/client/messaging',
    exact: true,
    component: () => <Redirect to={'/client/messaging/inbox'} />,
  },
  {
    path: [
      '/client/messaging/create-thread/:recipientId',
      '/client/messaging/create-thread/:recipientId/:jobId',
    ],
    component: React.lazy(
      () => import('../../../main/client/messaging/ClientCreateThreadPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: [
      '/client/messaging/:sectionId',
      '/client/messaging/:sectionId/:threadId',
    ],
    component: React.lazy(
      () => import('../../../main/client/messaging/ClientMessagingPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/notifications/:messageId',
    component: React.lazy(
      () =>
        import('../../../main/client/messaging/ClientNotificationLandingPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  // SETTINGS SECTION
  {
    path: '/client/settings',
    exact: true,
    component: () => <Redirect to={'/client/settings/general'} />,
  },
  {
    path: '/client/settings/:sectionId',
    component: React.lazy(
      () => import('../../../main/client/settings/ClientSettingsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  // COMPANY & USERS SECTION
  {
    path: '/client/company/main',
    component: React.lazy(
      () => import('../../../main/client/company/CompanyWrapper')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/company/invite/',
    component: React.lazy(
      () => import('../../../main/client/company/components/add-user/AddUser')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
  {
    path: '/client/company/editpermission/',
    component: React.lazy(
      () => import('../../../main/client/company/components/add-user/AddUser')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  // PAYMENTS SECTION
  {
    path: '/client/payments',
    exact: true,
    component: () => <Redirect to={'/client/payments/payments-list'} />,
  },
  {
    path: '/client/payments/:id',
    exact: true,
    component: React.lazy(
      () => import('../../../main/client/payments/ClientPaymentsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  {
    path: '/client/profile',
    exact: true,
    component: () => <Redirect to={'/client/profile/main'} />,
  },
  {
    path: '/client/profile/:id',
    component: React.lazy(
      () => import('../../../main/client/profile/ClientProfilePage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },

  {
    path: '/client/check-wrapper',
    component: React.lazy(
      () => import('../../../main/check-wrapper/CheckWrapper')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeClientGuard],
  },
];

export default clientRoutes;
